import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, Clock, Mail, Send, AlertTriangle, X } from 'lucide-react';
import api from '../../lib/api';
import SnoozeMenu from '../notify/SnoozeMenu';
import { previewText } from '../../lib/renderSafe';
import {
  cancelDismiss,
  formatSnoozeUntil,
  keepHeld,
  scheduleDismiss,
} from '../../lib/snoozeHold';

/**
 * MailNotifyBridge — the chime, the bottom-right mail toasts, and Snooze.
 * (mailbox-v3, 7 Sep 2026)
 *
 * Polls GET /api/notifications/mail/poll every POLL_MS and, for every mail
 * notification the backend routed to THIS user (routes/mail_notify.py decides
 * who gets what: recruiters hr@, Smita hr@+care@+smita@, the super admin
 * everything in and out), plays the chime and shows a toast with who it is
 * from, the subject and a short description, plus Snooze (iPhone presets) and
 * Open.
 *
 * `forced_chime: true` on a notification means it rings even when the user has
 * switched the chime off. Ruhshad: "EVERY MAIL RECEIVED WILL CHIME EVEN IF
 * SOMEONE HAS TURNED CHIME OFF." The toggle still exists and still governs
 * everything that is not forced.
 *
 * Mounted once from the page Header and once from the Mailbox page (which has
 * no Header); module-level state makes the extra mount harmless — only the
 * first live instance polls, and "already announced" ids are shared, so a
 * message never rings twice.
 */

export const POLL_MS = 30000;
const TOAST_MS = 12000;
const CHIME_KEY = 'dcs.mail.chime';

// Shared across mounts (see the note above).
const shared = {
  instances: 0,
  primary: null,
  since: null,
  announced: new Set(),
};

export const chimeEnabled = () => {
  try { return localStorage.getItem(CHIME_KEY) !== 'off'; } catch (e) { return true; }
};
export const setChimeEnabled = (on) => {
  try { localStorage.setItem(CHIME_KEY, on ? 'on' : 'off'); } catch (e) { /* private mode */ }
};

/** Two short notes through the Web Audio API — no asset, no 404. */
let audioCtx = null;
export function playChime(kind = 'in') {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    if (!audioCtx) audioCtx = new AudioCtx();
    if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
    const start = audioCtx.currentTime;
    const notes = kind === 'out' ? [[659.25, 0], [523.25, 0.12]] : [[880, 0], [1174.66, 0.12]];
    notes.forEach(([freq, offset]) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, start + offset);
      gain.gain.exponentialRampToValueAtTime(0.16, start + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + 0.28);
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(start + offset);
      osc.stop(start + offset + 0.32);
    });
  } catch (e) { /* a mailbox that cannot chime is still a mailbox */ }
}

// Browsers keep an AudioContext suspended until the first gesture.
if (typeof window !== 'undefined') {
  const unlock = () => { if (audioCtx && audioCtx.state === 'suspended') audioCtx.resume().catch(() => {}); };
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);
}

/** Play for a notification, honouring forced_chime over the user's toggle. */
export function chimeFor(notification) {
  const forced = !!notification?.forced_chime;
  if (!forced && !chimeEnabled()) return false;
  playChime(notification?.kind === 'mail_out' ? 'out' : 'in');
  return true;
}

export const SNOOZE_PRESETS = [
  { id: '1h', label: 'In 1 hour', preset: '1h' },
  { id: 'evening', label: 'This evening (18:00)', preset: 'evening' },
  { id: 'tomorrow', label: 'Tomorrow (09:00)', preset: 'tomorrow' },
  { id: 'custom', label: 'Pick a time…', preset: null },
];

export async function snoozeNotification(id, choice) {
  const body = choice.preset ? { preset: choice.preset } : { until: choice.until };
  const r = await api.post(`/notifications/${id}/snooze`, body);
  return r.data;
}

export const inboxLinkFor = (n) => {
  const params = new URLSearchParams();
  if (n?.thread_key) params.set('thread', n.thread_key);
  if (n?.mailbox) params.set('mailbox', n.mailbox);
  if (n?.mail_message_id) params.set('message', n.mail_message_id);
  const qs = params.toString();
  return `/ats/inbox${qs ? `?${qs}` : ''}`;
};

export const kindLabel = (n) => {
  switch (n?.kind || n?.type) {
    case 'mail_out': return 'Sent out';
    case 'mail_reminder': return 'Reminder';
    case 'mail_failed': return 'Not sent';
    case 'mail_snoozed_back': return 'Back in inbox';
    default: return 'New mail';
  }
};

const fromLine = (n) => {
  const f = n?.from || {};
  if (n?.kind === 'mail_out') return `${n.sent_by_email || 'someone'} → ${(n.to || []).join(', ')}`;
  return f.name ? `${f.name} <${f.email}>` : (f.email || 'someone');
};

/**
 * The snooze picker now lives in components/notify/SnoozeMenu.jsx: it holds the
 * card open while it is on screen (lib/snoozeHold.js suspends the auto-dismiss),
 * moves focus into itself, paints hover AND focus, and takes Escape for itself
 * so the panel behind stays put.  Re-exported here so anything that imported
 * `SnoozeMenu` from this module keeps working.
 */
export { SnoozeMenu };

/** One notification, as the Header bell and the toast both render it. */
export function MailNotificationRow({ n, compact = false, onSnoozed }) {
  const [menu, setMenu] = useState(false);
  const [state, setState] = useState('');
  const pick = async (choice) => {
    setMenu(false);
    setState('Snoozing…');
    try {
      const res = await snoozeNotification(n.id, choice);
      // Say what actually happened, using the time the backend chose,
      // rather than echoing back the button that was pressed.
      setState(formatSnoozeUntil(res && res.snoozed_until)
        || `Snoozed ${choice.label.toLowerCase()}`);
      if (onSnoozed) onSnoozed(n, choice);
    } catch (e) {
      setState('Could not snooze');
    }
  };
  return (
    <div className="min-w-0" data-testid={`mail-notification-${n.id}`}>
      <p className={`text-xs ${compact ? '' : 'font-semibold'} text-slate-900 truncate`}>
        <span className="inline-block mr-1 rounded px-1 py-px text-[10px] uppercase tracking-wide bg-amber-50 text-amber-800 border border-amber-200">
          {kindLabel(n)}
        </span>
        {n.mailbox_email || n.mailbox}
      </p>
      <p className="text-xs text-slate-700 truncate" title={fromLine(n)}>{fromLine(n)}</p>
      <p className="text-sm font-semibold text-slate-900 truncate" title={n.subject}>{n.subject || '(no subject)'}</p>
      {/* previewText: an HTML mail that opens with a <style> block used
          to put stylesheet source here. It falls back to the subject. */}
      {previewText(n.snippet, { fallback: n.subject, empty: '' }) ? (
        <p className="text-xs text-slate-500 line-clamp-2">
          {previewText(n.snippet, { fallback: n.subject, empty: '' })}
        </p>
      ) : null}
      <div className="relative mt-1 flex items-center gap-3 text-xs">
        <button
          type="button"
          className="text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
          onClick={(e) => { e.stopPropagation(); setMenu((m) => !m); }}
        >
          <Clock className="w-3 h-3" /> Snooze
        </button>
        {state ? (
          <span
            data-testid={`snooze-state-${n.id}`}
            className={state.indexOf('Could not') === 0
              ? 'text-red-600 font-medium'
              : 'text-emerald-700 font-medium'}
          >
            {state}
          </span>
        ) : null}
        {menu ? (
          <SnoozeMenu
            notificationId={n.id}
            align="left"
            onPick={pick}
            onClose={() => setMenu(false)}
          />
        ) : null}
      </div>
    </div>
  );
}

/** Bottom-right toast queue. */
function MailToasts({ items, onOpen, onDismiss, onSnoozed }) {
  if (!items.length) return null;
  return (
    <div className="fixed bottom-5 right-5 z-[70] flex flex-col gap-2 w-[22rem] max-w-[92vw]" data-testid="mail-toasts">
      {items.map((n) => (
        <div key={n.id} className="rounded-xl shadow-lg border border-slate-200 bg-white p-3 flex items-start gap-2.5">
          <span className={`mt-0.5 flex-shrink-0 rounded-full p-1.5 ${n.kind === 'mail_failed' ? 'bg-red-50' : n.kind === 'mail_out' ? 'bg-sky-50' : 'bg-amber-50'}`}>
            {n.kind === 'mail_failed' ? <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
              : n.kind === 'mail_out' ? <Send className="w-3.5 h-3.5 text-sky-600" />
                : <Mail className="w-3.5 h-3.5 text-amber-600" />}
          </span>
          <div className="min-w-0 flex-1">
            {/* The card stays for a beat after a pick so the
                confirmation is readable; SnoozeMenu's release arms a
                short timer for exactly that. */}
            <MailNotificationRow n={n} onSnoozed={(x, c) => onSnoozed(x, c)} />
            <button type="button" className="mt-1 text-xs font-medium text-blue-700 hover:underline" onClick={() => onOpen(n)}>
              Open
            </button>
          </div>
          <button type="button" aria-label="Dismiss" onClick={() => onDismiss(n.id)}>
            <X className="w-3.5 h-3.5 text-slate-400" />
          </button>
        </div>
      ))}
    </div>
  );
}

/** Small toggle for a toolbar: chime on/off (forced mail still rings). */
export function ChimeToggle({ className = '' }) {
  const [on, setOn] = useState(chimeEnabled());
  return (
    <button
      type="button"
      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 ${className}`}
      title="Mail from your mailboxes always chimes. This switch covers everything else."
      onClick={() => { setChimeEnabled(!on); setOn(!on); if (!on) playChime(); }}
      data-testid="chime-toggle"
    >
      {on ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
      {on ? 'Chime on' : 'Chime off (mail still rings)'}
    </button>
  );
}

export default function MailNotifyBridge({ onNotification }) {
  const navigate = useNavigate();
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(null);
  const cbRef = useRef(onNotification);
  cbRef.current = onNotification;

  const dismiss = useCallback((id) => {
    cancelDismiss(id);
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const announce = useCallback((rows) => {
    const fresh = rows.filter((n) => n && n.id && !shared.announced.has(n.id));
    if (!fresh.length) return;
    fresh.forEach((n) => shared.announced.add(n.id));
    // One chime per batch, however many arrived together.
    chimeFor(fresh.find((n) => n.forced_chime) || fresh[0]);
    // keepHeld: a card whose snooze menu is open is never sliced away by
    // the next batch, and it keeps its object identity, so React does not
    // re-key or remount the row underneath an open menu.
    setToasts((prev) => keepHeld(prev, [...fresh.map((n) => ({ ...n })), ...prev], 5));
    // The auto-dismiss lives in lib/snoozeHold.js now: suspended while a
    // snooze menu is open, restored untouched if nothing was chosen.
    fresh.forEach((n) => scheduleDismiss(n.id, TOAST_MS, () => dismiss(n.id)));
    // NotifyCenterBridge owns the OS notification for every kind of ATS event,
    // mail included (routes/mail_notify.py writes its rows into the same
    // `notifications` collection that /api/notify-center/feed reads). Firing
    // here as well would show the same mail twice, so this bridge now keeps
    // only what is its own: the chime, the toast and Snooze.
    if (cbRef.current) cbRef.current(fresh);
  }, [dismiss, navigate]);

  useEffect(() => {
    shared.instances += 1;
    const myId = shared.instances;
    idRef.current = myId;
    if (shared.primary === null) shared.primary = myId;
    let stopped = false;

    const tick = async () => {
      if (stopped || shared.primary !== myId) return;
      try {
        const r = await api.get('/notifications/mail/poll', { params: shared.since ? { since: shared.since } : {} });
        const data = r.data || {};
        if (shared.since) announce(data.new || []);
        shared.since = data.now || new Date().toISOString();
      } catch (e) { /* offline or logged out — try again next tick */ }
    };
    tick();
    const interval = setInterval(tick, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(interval);
      if (shared.primary === myId) shared.primary = null;
    };
  }, [announce]);

  // If the primary unmounts, the next surviving instance takes over on its tick.
  useEffect(() => {
    const handle = setInterval(() => {
      if (shared.primary === null && idRef.current !== null) shared.primary = idRef.current;
    }, 2000);
    return () => clearInterval(handle);
  }, []);

  const open = (n) => {
    dismiss(n.id);
    api.post(`/notifications/${n.id}/read`).catch(() => {});
    navigate(inboxLinkFor(n));
  };

  return <MailToasts items={toasts} onOpen={open} onDismiss={dismiss} onSnoozed={() => {}} />;
}
